/**
 * /calendar/day (Ted #20 Part A → #940; the list every #919 number opens;
 * the #922 snapshot numbers as taps into it).
 *
 * Ted, 09-09: *"We can easily move forward or backward to another date …
 * This calendar is imperative operationally. Jorge looks at it when he
 * comes in each morning to see what grooming he has scheduled."*
 *
 * The page owns ONE read — `/rollup` for the chosen date — and hands the
 * same payload to the snapshot strip and the list, so the strip's numbers
 * and the rows beneath them can never disagree (Principle 1). Filters live
 * in the URL (`?date=&service=&view=`), which is how a tapped rollup cell
 * arrives here pre-filtered and how a phone bookmark keeps its day.
 *
 * Reachable by every role that can read the dashboard (`dashboard:read`;
 * the server gate on `/rollup` is the same DASHBOARD_READ as `/today`).
 * "Only my schedule" (a specialist's own legs) waits on #940 Part C — the
 * conversation Ted asked to have first.
 */
import { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { readSession, todayIsoDate } from '../lib/api.js';
import { useAuth } from '../contexts/AuthContext.js';
// Sawyer 02 kennel-stopgap (A1-stopgap)
import { effectiveRolesOf } from '../lib/rbac-matrix.js';
import {
  canOpenMedsFeeding,
  MEDS_FEEDING_LABEL,
  medsFeedingHref,
} from '../lib/meds-feeding-link.js';
import { useBookingRefresh } from '../contexts/BookingRefreshContext.js';
import {
  fetchDashboardDataStatus,
  fetchDashboardRollup,
  isScheduleDataSuspect,
} from '../components/dashboard/dashboard-api.js';
import { useDashboardResource } from '../components/dashboard/useDashboardResource.js';
import { DayList } from '../components/dashboard/DayList.js';
import {
  dayListHref,
  formatDayLabel,
  isIsoDate,
  parseDayView,
  shiftIsoDate,
  type DayListFilter,
  type DayView,
} from '../components/dashboard/day-list-filters.js';
import './calendar-day.css';

type CalendarDayProps = {
  config: ClientConfig;
};

function SnapshotChip({
  label,
  value,
  view,
  date,
  active,
  suspect,
  testid,
}: {
  label: string;
  value: number;
  view: DayView;
  date: string;
  active: boolean;
  suspect: boolean;
  testid: string;
}): JSX.Element {
  const display = suspect && value === 0 ? '—' : String(value);
  return (
    <Link
      to={dayListHref({ date, view })}
      className={`ferro-calday__snap${active ? ' ferro-calday__snap--active' : ''}${
        suspect ? ' ferro-calday__snap--stale' : ''
      }`}
      aria-current={active ? 'true' : undefined}
      data-testid={testid}
    >
      <span className="ferro-calday__snap-value">{display}</span>
      <span className="ferro-calday__snap-label">{label}</span>
    </Link>
  );
}

export function CalendarDay({ config }: CalendarDayProps): JSX.Element {
  const { token } = readSession();
  const { isValidatingSession, staff } = useAuth();
  const apiUrl = config.deployment.apiUrl;
  // Sawyer 02 kennel-stopgap (A1-stopgap) — "Meds & feeding" sits in this header for every role
  // that can open its target (the seam in lib/meds-feeding-link.ts).
  const showMedsFeeding = canOpenMedsFeeding(effectiveRolesOf(staff));
  const [searchParams, setSearchParams] = useSearchParams();

  const rawDate = searchParams.get('date');
  const date = isIsoDate(rawDate) ? rawDate : todayIsoDate();
  const filter: DayListFilter = {
    serviceTypeId: searchParams.get('service'),
    view: parseDayView(searchParams.get('view')),
  };

  const { version: bookingRefreshVersion } = useBookingRefresh();
  const fetchesEnabled = Boolean(token) && !isValidatingSession;
  // Re-read when the date changes or the shell's Quick Book lands a booking
  // (the hook keys on this number; the date itself lives in the closure).
  const refreshKey = bookingRefreshVersion + hashDate(date);
  const rollup = useDashboardResource(
    () => fetchDashboardRollup(apiUrl, token ?? '', date),
    refreshKey,
    fetchesEnabled,
  );
  const dataStatus = useDashboardResource(
    () => fetchDashboardDataStatus(apiUrl, token ?? '', date),
    refreshKey,
    fetchesEnabled,
  );
  const scheduleSuspect =
    dataStatus.data !== null && isScheduleDataSuspect(dataStatus.data);

  const setFilter = useCallback(
    (next: DayListFilter) => {
      const params = new URLSearchParams({ date });
      if (next.serviceTypeId) params.set('service', next.serviceTypeId);
      if (next.view !== 'all') params.set('view', next.view);
      setSearchParams(params, { replace: true });
    },
    [date, setSearchParams],
  );

  const goTo = (iso: string): void => {
    const params = new URLSearchParams(searchParams);
    params.set('date', iso);
    setSearchParams(params);
  };

  if (!token) {
    return (
      <section className="ferro-admin-page">
        <h1 className="ferro-section__title">Day list</h1>
        <p className="ferro-muted">Please sign in to continue.</p>
      </section>
    );
  }

  const snap = rollup.data?.snapshot ?? null;
  const isToday = date === todayIsoDate();

  return (
    <section className="ferro-admin-page ferro-calday" data-testid="calendar-day">
      <header className="ferro-calday__header">
        <div className="ferro-calday__nav">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-calday__nav-btn"
            onClick={() => goTo(shiftIsoDate(date, -1))}
            aria-label="Previous day"
            data-testid="calendar-day-prev"
          >
            ‹
          </button>
          <div className="ferro-calday__date">
            <h1 className="ferro-calday__title" data-testid="calendar-day-title">
              {formatDayLabel(date)}
              {isToday ? <span className="ferro-calday__today"> · Today</span> : null}
            </h1>
            <label className="ferro-calday__picker">
              <span className="ferro-calday__sr">Pick a date</span>
              <input
                type="date"
                value={date}
                onChange={(e) => {
                  if (isIsoDate(e.target.value)) goTo(e.target.value);
                }}
                data-testid="calendar-day-input"
              />
            </label>
          </div>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost ferro-calday__nav-btn"
            onClick={() => goTo(shiftIsoDate(date, 1))}
            aria-label="Next day"
            data-testid="calendar-day-next"
          >
            ›
          </button>
        </div>
        <div className="ferro-calday__actions">
        {!isToday ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => goTo(todayIsoDate())}
            data-testid="calendar-day-today"
          >
            Back to today
          </button>
        ) : null}
          {/* Sawyer 02 kennel-stopgap (A1-stopgap) — outline, not filled: the Day list keeps no
              filled primary (Principle 2). Static link, so it renders through
              the rollup's loading and error states too. */}
          {showMedsFeeding ? (
            <Link
              className="ferro-btn ferro-btn--outline ferro-calday__meds"
              to={medsFeedingHref(date, 'day-list', {
                service: filter.serviceTypeId,
                view: filter.view,
              })}
              data-testid="calendar-day-meds-feeding"
            >
              {MEDS_FEEDING_LABEL}
            </Link>
          ) : null}
        </div>
      </header>

      {dataStatus.data?.warning ? (
        <div className="ferro-data-banner" role="status" data-testid="calendar-day-data-banner">
          <p>{dataStatus.data.warning}</p>
        </div>
      ) : null}

      {/* Ted #18 — the four numbers, each a tap into the list beneath. */}
      <div className="ferro-calday__snapshot" data-testid="calendar-day-snapshot">
        <SnapshotChip
          label="On site now"
          value={snap?.onSiteNow ?? 0}
          view="onSite"
          date={date}
          active={filter.view === 'onSite'}
          suspect={scheduleSuspect}
          testid="snapshot-on-site"
        />
        <SnapshotChip
          label="Arriving"
          value={snap?.arriving ?? 0}
          view="arriving"
          date={date}
          active={filter.view === 'arriving'}
          suspect={scheduleSuspect}
          testid="snapshot-arriving"
        />
        <SnapshotChip
          label="Going home"
          value={snap?.goingHome ?? 0}
          view="goingHome"
          date={date}
          active={filter.view === 'goingHome'}
          suspect={scheduleSuspect}
          testid="snapshot-going-home"
        />
        <SnapshotChip
          label="Overnight tonight"
          value={snap?.overnightTonight ?? 0}
          view="overnight"
          date={date}
          active={filter.view === 'overnight'}
          suspect={scheduleSuspect}
          testid="snapshot-overnight"
        />
      </div>

      {rollup.isLoading && !rollup.data ? (
        <div className="ferro-calday__loading" role="status" aria-live="polite" data-testid="calendar-day-loading">
          <div className="ferro-loading-screen__spinner" aria-hidden="true" />
          <p className="ferro-loading-screen__text">Loading the day…</p>
        </div>
      ) : rollup.error && !rollup.data ? (
        <div className="ferro-calday__error" role="alert" data-testid="calendar-day-error">
          <span>Couldn’t load this day.</span>
          <button type="button" className="ferro-btn ferro-btn--ghost" onClick={rollup.retry}>
            Retry
          </button>
        </div>
      ) : (
        <DayList
          items={rollup.data?.items ?? []}
          services={rollup.data?.services ?? []}
          filter={filter}
          onFilterChange={setFilter}
          dataSuspect={scheduleSuspect}
        />
      )}
    </section>
  );
}

/** A stable small integer per date so a date change bumps the resource key. */
function hashDate(iso: string): number {
  let h = 0;
  for (let i = 0; i < iso.length; i += 1) h = (h * 31 + iso.charCodeAt(i)) % 1_000_003;
  return h * 1000;
}
